// Wrapper tipado sobre HttpClient para centralizar las llamadas HTTP
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../../domain/interfaces/api-response.model';

type QueryParams = Record<string, string | number | boolean>;
/**
 * Wrapper tipado sobre HttpClient.
 * Centraliza la construcción de URLs y headers, y propaga errores de forma uniforme.
 * Los tipos de respuesta se definirán al implementar cada pantalla.
 */
@Injectable({ providedIn: 'root' })
export class HttpClientService {
  private readonly http = inject(HttpClient);

  /** Realiza una petición GET tipada */
  get<T>(url: string, params?: QueryParams): Observable<ApiResponse<T>> {
    const httpParams = this.buildParams(params);
    return this.http.get<ApiResponse<T>>(url, { params: httpParams });
  }

  /** Realiza una petición POST tipada */
  post<T>(url: string, body: unknown): Observable<ApiResponse<T>> {
    return this.http.post<ApiResponse<T>>(url, body);
  }

  /**
   * Para endpoints (como /clientes/autos/lead) cuya respuesta cruda no sigue ese contrato.
   */
  postRaw<T>(url: string, body: unknown): Observable<T> {
    return this.http.post<T>(url, body);
  }

  getRaw<T>(url: string, params?: QueryParams): Observable<T> {
    const httpParams = this.buildParams(params);
    return this.http.get<T>(url, { params: httpParams });
  }

  /** Realiza una petición PUT tipada */
  put<T>(url: string, body: unknown): Observable<ApiResponse<T>> {
    return this.http.put<ApiResponse<T>>(url, body);
  }

  /** Realiza una petición PATCH tipada */
  patch<T>(url: string, body: unknown): Observable<ApiResponse<T>> {
    return this.http.patch<ApiResponse<T>>(url, body);
  }

  /** Realiza una petición DELETE tipada */
  delete<T>(url: string): Observable<ApiResponse<T>> {
    return this.http.delete<ApiResponse<T>>(url);
  }

  /** Construye los parámetros de URL */
  private buildParams(params?: QueryParams): HttpParams {
    let httpParams = new HttpParams();
    if (!params) return httpParams;
    Object.entries(params).forEach(([key, value]) => {
      httpParams = httpParams.set(key, String(value));
    });
    return httpParams;
  }
}
